/**
* QosPlugin.ts
* @author Hector Hernandez (hectorh)
* @copyright Microsoft 2018
* File containing QoS plugin.
*/
import { __extends } from "tslib";
import dynamicProto from "@microsoft/dynamicproto-js";
import { Extensions, RemoteDependencyDataType, _throwInternal, cfgDfFunc, cfgDfMerge, createGuid, createProcessTelemetryContext, isFunction, isNullOrUndefined, isNumber, isValueAssigned, objForEachKey, onConfigChange, setProcessTelemetryTimings } from "@microsoft/applicationinsights-core-js";
import { AjaxPlugin } from "@microsoft/applicationinsights-dependencies-js";
import { objDeepFreeze } from "@nevware21/ts-utils";
import { Id } from "./Id";
import { _collectMetaTags, _getMetaData, _getPageName, _getUri } from "./common/DataCollector";
import { IncomingQosEvent } from "./events/IncomingQosEvent";
import { OutgoingQosEvent } from "./events/OutgoingQosEvent";
var MS_CV = "MS-CV";
var defaultQosPluginConfig = objDeepFreeze({
    enableCorsCorrelation: false,
    correlationHeaderExcludedDomains: [],
    correlationHeaderExcludePatterns: undefined,
    disableCorrelationHeaders: false,
    // Unlimited autocollection
    maxAjaxCallsPerView: -1,
    disableAjaxTracking: false,
    disableFetchTracking: false,
    enableCvHeaders: false,
    appId: undefined,
    excludeRequestFromAutoTrackingPatterns: undefined,
    distributedTracingMode: 1 /* eDistributedTracingModes.AI_AND_W3C */,
    correlationHeaderDomains: undefined,
    enableRequestHeaderTracking: false,
    enableResponseHeaderTracking: false,
    enableAjaxErrorStatusText: false,
    enableAjaxPerfTracking: false,
    maxAjaxPerfLookupAttempts: 3,
    ajaxPerfLookupDelay: 25,
    ignoreHeaders: [
        "Authorization",
        "X-API-Key",
        "WWW-Authenticate"
    ],
    addRequestContext: undefined,
    callback: cfgDfMerge({
        pageName: cfgDfFunc(),
        id: cfgDfFunc()
    }),
    autoCapture: cfgDfMerge({
        msTags: true
    }),
    // overrideValues SDK should use instead of collecting automatically
    coreData: cfgDfMerge({
        requestUri: "",
        pageName: ""
    }),
});
var QosPlugin = /** @class */ (function (_super) {
    __extends(QosPlugin, _super);
    /**
     * @constructor
     */
    function QosPlugin() {
        var _this = _super.call(this) || this;
        _this.identifier = "QosPlugin";
        _this.version = '4.4.1';
        var _cvPlugin;
        var _outgoingQosEvent;
        var _incomingQosEvent;
        var _metaTags;
        var theConfig;
        dynamicProto(QosPlugin, _this, function (_self, _base) {
            _initDefaults();
            /**
             * Override of parent method, executed when auto capture is enabled for XMLHttpRequest or Fetch API
             * @param ajaxData - Outgoing Ajax call.
             * @param input - Fetch API request input
             * @param init - Fetch API initiator
             * @param xhr - XMLHttpRequest
             */
            _self.includeCorrelationHeaders = function (ajaxData, input, init, xhr) {
                var dependencyRequest = _base.includeCorrelationHeaders(ajaxData, input, init, xhr);
                if (theConfig.enableCvHeaders) {
                    var cv = _self.getCv();
                    if (cv && (input || xhr)) {
                        // Only try to increment if we have a fetch or xhr
                        cv.increment();
                        var cvValue = cv.getValue();
                        if (input) { // Fetch
                            if (!dependencyRequest) {
                                // reuse the past init if available and no dependencyRequest was returned, so that
                                // we correctly reference the init.headers
                                dependencyRequest = init || {};
                            }
                            // dependencyRequest headers overrides original request headers
                            // so, if they exist use only them, otherwise use request's because they should have been applied in the first place
                            // not using original request headers will result in them being lost
                            if (!dependencyRequest.headers || !isFunction(dependencyRequest.headers.append)) {
                                dependencyRequest.headers = new Headers(dependencyRequest.headers || (input instanceof Request ? (input.headers || {}) : {}));
                            }
                            dependencyRequest.headers.append(MS_CV, cvValue);
                        }
                        else { // XHR
                            dependencyRequest.setRequestHeader(MS_CV, cvValue);
                        }
                    }
                }
                return dependencyRequest;
            };
            /**
             * Initialize the Qos plugin
             * @param config - The plugin configuration.
             * @param core - Core reference.
             * @param extensions - All other extensions
             */
            _self.initialize = function (coreConfig, core, extensions) {
                _self._addHook(onConfigChange(coreConfig, function (details) {
                    var coreConfig = details.cfg;
                    var ctx = createProcessTelemetryContext(null, coreConfig, core);
                    theConfig = _self._qosConfig = ctx.getExtCfg(_this.identifier, defaultQosPluginConfig);
                    // Update parent config
                    coreConfig.extensionConfig[AjaxPlugin.identifier] = theConfig;
                    var metaTags = _collectMetaTags(_self._qosConfig);
                    _metaTags.market = _getMetaData(metaTags, theConfig.coreData, "market");
                    _metaTags.serverImpressionGuid = _getMetaData(metaTags, theConfig.coreData, "serverImpressionGuid");
                }));
                _self["_getDbgPlgTargets"] = function () {
                    return _metaTags.market;
                };
                _base.initialize(coreConfig, core, extensions);
                // Iterate other loaded extensions
                var correlationVectorExtension = (core.getPlugin("CorrelationVectorPlugin") || {}).plugin;
                var webAnalyticsExtension = (core.getPlugin("WebAnalyticsPlugin") || {}).plugin;
                // Use WebAnalytics Id if available
                if (webAnalyticsExtension && webAnalyticsExtension.id) {
                    _self.id = webAnalyticsExtension.id;
                }
                else {
                    _self.id = new Id(core);
                }
                if (theConfig.enableCvHeaders) {
                    if (correlationVectorExtension) {
                        theConfig.enableCvHeaders = true;
                        _cvPlugin = correlationVectorExtension;
                    }
                    else {
                        _throwInternal(_self.diagLog(), 2 /* eLoggingSeverity.WARNING */, 508 /* _eExtendedInternalMessageId.CVPluginNotAvailable */, "Enable cV headers it is set to \"true\" in config.  However, cv plugin is not available. Disabling adding Cv in dependency requests ");
                        theConfig.enableCvHeaders = false;
                    }
                }
            };
            /**
             * Process a given event.
             * @param env - This is the current event being reported
             * @param itemCtx - This is the context for the current request, ITelemetryPlugin instances
             * can optionally use this to access the current core instance or define / pass additional information
             * to later plugins (vs appending items to the telemetry item)
             */
            _self.processTelemetry = function (evt, itemCtx) {
                setProcessTelemetryTimings(evt, _self.identifier);
                var event = evt;
                switch (event.baseType) {
                    case RemoteDependencyDataType:
                        if (event.name !== "Ms.Qos.OutgoingServiceRequest") {
                            event.name = "Ms.Web.OutgoingRequest";
                            event.baseData = event.baseData || {};
                            event.baseData.properties = event.baseData.properties || {};
                            event.baseData.properties["parentId"] = event.baseData.properties["parentId"] || _self.id.getLastPageViewId();
                            // Remove extra AI properties
                            delete (event.baseData["method"]);
                        }
                        event.latency = 1 /* EventLatencyValue.Normal */;
                        break;
                    default:
                        break;
                }
                _self.processNext(event, itemCtx);
            };
            /**
            * Track DependencyData event
            * @param dependency -DependencyData event
            * @param properties - DependencyData properties
            */
            _self.trackDependencyData = function (dependency, properties) {
                _self.trackDependencyDataInternal(dependency, properties);
            };
            /**
            * Track DependencyData event internal (Called by automatic tracking scenarios)
            * @param dependency -DependencyData event
            * @param properties - DependencyData properties
            */
            _self.trackDependencyDataInternal = function (dependency, properties, systemProperties) {
                // Part A
                var ext = systemProperties || {};
                var traceExt = ext[Extensions.TraceExt];
                if (traceExt) {
                    // copy any application insights trace properties (set by the dependencies module) to the distributed trace extension
                    // Don't remove the trace fields as there are a few partners that have both AI and 1DS, if not needed these will be removed
                    // later.
                    var dtExt = ext.dt = ext.dt || {};
                    dtExt.traceId = dtExt.traceId || traceExt.traceID;
                    dtExt.spanId = dtExt.spanId || traceExt.parentID;
                    if (!isNullOrUndefined(traceExt.traceFlags) && !isNumber(dtExt.traceFlags)) {
                        dtExt.traceFlags = traceExt.traceFlags;
                    }
                }
                if (isValueAssigned(dependency.cV)) {
                    ext["mscv"] = {};
                    ext["mscv"]["cV"] = dependency.cV;
                }
                dependency.id = theConfig.callback && theConfig.callback.id ? theConfig.callback.id() : createGuid();
                // Part C
                properties = properties || {};
                properties.pageName = isValueAssigned(properties.pageName) ? properties.pageName : _getPageName(_self._qosConfig);
                properties.uri = isValueAssigned(properties.uri) ? properties.uri : _getUri(_self._qosConfig);
                properties.market = isValueAssigned(properties.market) ? properties.market : _metaTags.market;
                properties.serverImpressionGuid = isValueAssigned(properties.serverImpressionGuid) ? properties.serverImpressionGuid : _metaTags.serverImpressionGuid;
                _base.trackDependencyDataInternal(dependency, properties, ext);
            };
            /**
             * Track Service QoS event
             * @param eventData - IExtendedTelemetryItem event
             * @param properties - Custom event properties (part C)
             */
            function _trackServiceQos(event, customProperties) {
                _addCustomProperties(event, customProperties);
                _self.core.track(event);
            }
            /**
             * Track OutgoingServiceRequest event
             * @param eventData - OutgoingServiceRequest event data
             * @param properties - Custom event properties (part C)
             */
            _self.trackServiceOutgoingQos = function (eventData, customProperties) {
                var event = {
                    name: "Ms.Qos.OutgoingServiceRequest",
                    baseType: "RemoteDependencyData",
                    ext: {},
                    baseData: {},
                    data: {},
                    latency: 3 /* EventLatencyValue.RealTime */
                };
                _outgoingQosEvent._setQosProperties(event, eventData);
                _addCustomProperties(event, customProperties);
                _trackServiceQos(event);
            };
            /**
             * Track IncomingServiceRequest event
             * @param eventData - IncomingServiceRequest event data
             */
            _self.trackServiceIncomingQos = function (eventData, customProperties) {
                var event = {
                    name: "Ms.Qos.IncomingServiceRequest",
                    baseType: "RequestData",
                    ext: {},
                    baseData: {},
                    data: {},
                    latency: 3 /* EventLatencyValue.RealTime */
                };
                _incomingQosEvent._setQosProperties(event, eventData);
                _addCustomProperties(event, customProperties);
                _trackServiceQos(event);
            };
            _self.getCv = function () {
                if (_cvPlugin) {
                    return _cvPlugin.getCv();
                }
                return null;
            };
            _self._doTeardown = function (unloadCtx, unloadState) {
                _base._doTeardown(unloadCtx, unloadState);
                _initDefaults();
            };
            function _initDefaults() {
                _cvPlugin = null;
                _metaTags = { market: "", serverImpressionGuid: "" };
                theConfig = {};
                _outgoingQosEvent = new OutgoingQosEvent();
                _incomingQosEvent = new IncomingQosEvent();
            }
            function _addCustomProperties(event, customProperties) {
                // Add extra Part C
                if (isValueAssigned(customProperties)) {
                    objForEachKey(customProperties, function (prop, value) {
                        event.data[prop] = value;
                    });
                }
            }
        });
        return _this;
    }
    /**
     * Override of parent method, executed when auto capture is enabled for XMLHttpRequest or Fetch API
     * @param ajaxData - Outgoing Ajax call.
     * @param input - Fetch API request input
     * @param init - Fetch API initiator
     * @param xhr - XMLHttpRequest
     */
    QosPlugin.prototype.includeCorrelationHeaders = function (ajaxData, input, init, xhr) {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
        return null;
    };
    /**
     * Initialize the Qos plugin
     * @param config - The plugin configuration.
     * @param core - Core reference.
     * @param extensions - All other extensions
     */
    QosPlugin.prototype.initialize = function (coreConfig, core, extensions) {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
    };
    /**
     * Process a given event.
     * @param env - This is the current event being reported
     * @param itemCtx - This is the context for the current request, ITelemetryPlugin instances
     * can optionally use this to access the current core instance or define / pass additional information
     * to later plugins (vs appending items to the telemetry item)
     */
    QosPlugin.prototype.processTelemetry = function (evt, itemCtx) {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
    };
    /**
    * Track DependencyData event
    * @param dependency -DependencyData event
    * @param properties - DependencyData properties
    */
    QosPlugin.prototype.trackDependencyData = function (dependency, properties) {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
    };
    /**
     * Track OutgoingServiceRequest event
     * @param eventData - OutgoingServiceRequest event data
     * @param properties - Custom event properties (part C)
     */
    QosPlugin.prototype.trackServiceOutgoingQos = function (eventData, customProperties) {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
    };
    /**
     * Track IncomingServiceRequest event
     * @param eventData - IncomingServiceRequest event data
     */
    QosPlugin.prototype.trackServiceIncomingQos = function (eventData, customProperties) {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
    };
    /**
     * This function is deprecated, will be removed after v4.4.0
     * @deprecated
     */
    QosPlugin.prototype.getCv = function () {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
        return null;
    };
    /**
    * Track DependencyData event internal (Called by automatic tracking scenarios)
    * @param dependency -DependencyData event
    * @param properties - DependencyData properties
    */
    QosPlugin.prototype.trackDependencyDataInternal = function (dependency, properties, systemProperties) {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
    };
    return QosPlugin;
}(AjaxPlugin));
export { QosPlugin };
//# sourceMappingURL=QosPlugin.js.map